// ========================================
// COMPONENTE: CAMPANHAS
// src/components/Campanhas.jsx
// ======================================
// ======================================

import React, { useState, useEffect } from 'react';
import { Trophy, Target, TrendingUp, Users, Award, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import { pedidoAPI } from '../services/api';
import Loading from './Loading';

const Campanhas = () => {
  const { usuario, isAdminOuDiretoria } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState([]);
  const [erro, setErro] = useState('');
  const [totais, setTotais] = useState({
    desafioJunho: 0,
    realizadoJunho: 0,
    desafioDezembro: 0,
    realizadoDezembro: 0
  });

  // Verificar se pode ver todos (Admin/Diretoria/Administrativo)
  const podeVerTodos = () => {
    if (!usuario) return false;
    const dep = (usuario.departamento || '').toLowerCase().trim();
    return dep === 'admin' || dep === 'diretoria' || dep === 'administrativo' || dep === 'administrador';
  };

  useEffect(() => {
    carregarDados();
  }, []);

  // Função para ordenar por departamento
  const ordenarPorDepartamento = (dados) => {
    const ordemDepartamento = {
      'administrativo': 1,
      'administrador': 1,
      'admin': 2,
      'diretoria': 3,
      'vendas': 4,
      'representante': 5
    };
    
    return [...dados].sort((a, b) => {
      const depA = (a.departamento || '').toLowerCase().trim();
      const depB = (b.departamento || '').toLowerCase().trim();
      const ordemA = ordemDepartamento[depA] || 99;
      const ordemB = ordemDepartamento[depB] || 99;
      
      if (ordemA !== ordemB) return ordemA - ordemB;
      // Se mesmo departamento, ordena por nome
      return (a.nome_completo || '').localeCompare(b.nome_completo || '');
    });
  };

  const normalizarTexto = (valor) => String(valor || '').trim().toLowerCase();

  const pedidoPertenceAoParticipante = (pedido, participante) => {
    const vendedor = normalizarTexto(pedido.vendedor);
    const nome = normalizarTexto(participante.nome_completo);
    const username = normalizarTexto(participante.username_ldap);
    const codigo = String(participante.codigo_id || '').trim();

    if (!vendedor) return false;
    if (vendedor === nome || vendedor === username) return true;
    if (nome && vendedor.includes(nome)) return true;
    if (username && vendedor.includes(username)) return true;
    if (codigo && vendedor.endsWith(` ${codigo}`)) return true;

    return false;
  };

  const calcularPrecoMedioDashboard = (pedidos) => {
    const totalM2 = pedidos.reduce((acc, pedido) => acc + (parseFloat(pedido.total_m2_geral) || 0), 0);
    const totalVendas = pedidos.reduce(
      (acc, pedido) => acc + (parseFloat(pedido.valor_tabela1) || 0) + (parseFloat(pedido.valor_tabela2) || 0) + (parseFloat(pedido.valor_produtos_especiais) || 0),
      0
    );

    return totalM2 > 0 ? totalVendas / totalM2 : 0;
  };

  const aplicarLogicaPrecoMedioDashboard = (participantes, pedidosDashboard) => {
    const pedidosFaturados = pedidosDashboard.filter((pedido) => pedido.fase_atual === 'PEDIDO_FATURADO');

    return participantes.map((participante) => {
      const pedidosConsiderados = participante.forma_analise === 'Geral'
        ? pedidosFaturados
        : pedidosFaturados.filter((pedido) => pedidoPertenceAoParticipante(pedido, participante));

      return {
        ...participante,
        preco_medio_realizado: calcularPrecoMedioDashboard(pedidosConsiderados)
      };
    });
  };

  const carregarDados = async () => {
    setLoading(true);
    setErro('');

    try {
      const [response, pedidosDashboard] = await Promise.all([
        api.get('/campanhas'),
        pedidoAPI.buscarDadosDashboard('1ano')
      ]);
      
      if (response.success) {
        const dadosComPrecoDashboard = aplicarLogicaPrecoMedioDashboard(response.dados || [], pedidosDashboard || []);
        // Ordenar por departamento antes de setar
        const dadosOrdenados = ordenarPorDepartamento(dadosComPrecoDashboard);
        setDados(dadosOrdenados);
        setTotais(response.totais || {
          desafioJunho: 0,
          realizadoJunho: 0,
          desafioDezembro: 0,
          realizadoDezembro: 0
        });
      } else {
        setErro(response.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      setErro(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Formatar valor monetário
  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  // Calcular percentual
  const calcularPercentual = (realizado, meta) => {
    if (!meta || meta === 0) return 0;
    return ((realizado / meta) * 100).toFixed(1);
  };

  // Cor do percentual baseado no desempenho
  const corPercentual = (percentual) => {
    if (percentual >= 100) return 'text-green-600 bg-green-100';
    if (percentual >= 75) return 'text-blue-600 bg-blue-100';
    if (percentual >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  // Barra de progresso
  const BarraProgresso = ({ percentual, cor }) => (
    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
      <div 
        className={`h-2.5 rounded-full transition-all duration-500 ${
          percentual >= 100 ? 'bg-green-500' :
          percentual >= 75 ? 'bg-blue-500' :
          percentual >= 50 ? 'bg-yellow-500' : 'bg-red-500'
        }`}
        style={{ width: `${Math.min(percentual, 100)}%` }}
      />
    </div>
  );

  if (loading) {
    return <Loading texto="Carregando campanhas..." tamanho="lg" fullScreen={false} />;
  }

  if (erro) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg flex items-center gap-3">
          <AlertCircle size={24} />
          <p>{erro}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto pb-10 pt-4">
      {/* Header Principal - Estilo padrão do sistema */}
      <div 
        className="relative overflow-hidden rounded-2xl text-white p-8 mb-8 shadow-xl group cursor-default mt-2"
        style={{ 
          background: 'linear-gradient(135deg, #0D5DFC 0%, #0099cc 25%, #0D5DFC 50%, #0099cc 75%, #0D5DFC 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 8s ease infinite'
        }}
      >
        {/* Efeito de brilho no hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>
        
        <div className="flex items-center justify-between mb-4 relative z-10">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Trophy size={36} />
              Campanhas de Vendas
            </h1>
            <p className="text-blue-100">Acompanhe as metas e realizações de vendas</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold">{dados.length}</p>
            <p className="text-sm text-blue-100 uppercase tracking-wider">PARTICIPANTES</p>
          </div>
        </div>

        {/* Cards de Resumo dentro do header */}
        {podeVerTodos() && dados.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 relative z-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <Target size={18} className="text-white" />
                <span className="text-sm font-medium text-white/80">Desafio Junho</span>
              </div>
              <div className="text-2xl font-bold">{formatarValor(totais.desafioJunho)}</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-white" />
                <span className="text-sm font-medium text-white/80">Realizado Junho</span>
              </div>
              <div className="text-2xl font-bold">{formatarValor(totais.realizadoJunho)}</div>
              <p className="text-xs text-white/70 mt-1">
                {calcularPercentual(totais.realizadoJunho, totais.desafioJunho)}% da meta
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <Target size={18} className="text-white" />
                <span className="text-sm font-medium text-white/80">Desafio Dezembro</span>
              </div>
              <div className="text-2xl font-bold">{formatarValor(totais.desafioDezembro)}</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/20 hover:scale-105 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-white" />
                <span className="text-sm font-medium text-white/80">Realizado Ano</span>
              </div>
              <div className="text-2xl font-bold">{formatarValor(totais.realizadoDezembro)}</div>
              <p className="text-xs text-white/70 mt-1">
                {calcularPercentual(totais.realizadoDezembro, totais.desafioDezembro)}% da meta
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-8"></div>

      {/* Tabela de Campanhas */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header da tabela */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <Award size={24} className="text-white" />
            <div>
              <h2 className="text-lg font-bold text-white">DESEMPENHO DE VENDAS</h2>
              <p className="text-blue-100 text-xs">
                {podeVerTodos() ? 'Visualização de todos os participantes' : 'Seu desempenho nas campanhas'}
              </p>
            </div>
          </div>
        </div>

        {dados.length === 0 ? (
          <div className="p-8 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">Nenhum participante cadastrado</p>
            <p className="text-gray-400 text-sm mt-2">Configure as metas em Gerenciar Usuários</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-600 uppercase">Participante</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Desafio Jun</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Realizado Jun</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase">%</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Desafio Dez</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Realizado Ano</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase">%</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Inadimp.</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Preço Médio Esperado</th>
                  <th className="px-2 py-3 text-center text-xs font-bold text-gray-600 uppercase whitespace-nowrap">Preço Médio Realizado</th>
                </tr>
              </thead>
              <tbody>
                {dados.map((item, index) => {
                  const percentJunho = calcularPercentual(item.realizado_junho, item.meta_junho);
                  const percentDezembro = calcularPercentual(item.realizado_dezembro, item.meta_dezembro);
                  
                  return (
                    <tr key={item.id || index} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                            {(item.nome_completo || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 text-sm truncate max-w-[160px]">{item.nome_completo}</p>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-gray-500">{item.departamento}</span>
                              {item.forma_analise === 'Geral' && (
                                <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                  Geral
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {/* Desafio Junho */}
                      <td className="px-2 py-3 text-center">
                        <span className="font-semibold text-gray-700 text-sm">
                          {formatarValor(item.meta_junho)}
                        </span>
                      </td>
                      
                      {/* Realizado Junho */}
                      <td className="px-2 py-3 text-center">
                        <span className="font-semibold text-blue-600 text-sm">
                          {formatarValor(item.realizado_junho)}
                        </span>
                        <BarraProgresso percentual={percentJunho} />
                      </td>
                      
                      {/* % Junho */}
                      <td className="px-2 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${corPercentual(percentJunho)}`}>
                          {percentJunho}%
                        </span>
                      </td>
                      
                      {/* Desafio Dezembro */}
                      <td className="px-2 py-3 text-center">
                        <span className="font-semibold text-gray-700 text-sm">
                          {formatarValor(item.meta_dezembro)}
                        </span>
                      </td>
                      
                      {/* Realizado Dezembro */}
                      <td className="px-2 py-3 text-center">
                        <span className="font-semibold text-green-600 text-sm">
                          {formatarValor(item.realizado_dezembro)}
                        </span>
                        <BarraProgresso percentual={percentDezembro} />
                      </td>
                      
                      {/* % Dezembro */}
                      <td className="px-2 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${corPercentual(percentDezembro)}`}>
                          {percentDezembro}%
                        </span>
                      </td>
                      
                      {/* % Real Inadimplência - quanto menor melhor */}
                      <td className="px-2 py-3 text-center">
                        <span className={`px-1.5 py-0.5 rounded-full font-bold text-xs ${
                          (item.realizado_inadimplencia || 0) <= (item.meta_inadimplencia || 0) 
                            ? 'text-green-600 bg-green-100' 
                            : 'text-red-600 bg-red-100'
                        }`}>
                          {(item.realizado_inadimplencia || 0).toFixed(1)}%
                        </span>
                        <p className="text-[9px] text-gray-400 mt-0.5">
                          Máx: {(item.meta_inadimplencia || 0)}%
                        </p>
                      </td>
                      
                      {/* Preço Médio Esperado */}
                      <td className="px-2 py-3 text-center">
                        <span className="font-semibold text-gray-700 text-sm">
                          {formatarValor(item.media_venda_forro)}
                        </span>
                      </td>
                      
                      {/* Preço Médio Realizado - quanto maior melhor */}
                      <td className="px-2 py-3 text-center">
                        <span className={`px-1.5 py-0.5 rounded-full font-bold text-xs ${
                          (item.preco_medio_realizado || 0) >= (item.media_venda_forro || 0) 
                            ? 'text-green-600 bg-green-100' 
                            : 'text-red-600 bg-red-100'
                        }`}>
                          {formatarValor(item.preco_medio_realizado || 0)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Campanhas;
